import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../../../components/ui';
import { getProjectFundingRequests } from '../../../../services/fundingApi';
import { formatCurrency, formatDate } from '../../../../utils/formatters/statusFormatters';
import { TransformedProject } from '../hooks/useProjects';

interface FundingRequest {
  id: string;
  name?: string;
  program_name?: string;
  status: string;
  requested_amount?: number;
  approved_amount?: number;
  submission_date?: string;
  approval_date?: string;
}

interface Props {
  project: TransformedProject;
}

export const FundingRequestsSection: React.FC<Props> = ({ project }) => {
  const [loading, setLoading] = useState(true);
  const [fundingRequests, setFundingRequests] = useState<FundingRequest[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (project?.id) {
      loadFundingRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  const loadFundingRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      const requests: FundingRequest[] = await getProjectFundingRequests(project.id);
      setFundingRequests(requests ?? []);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('Error loading funding requests:', err);
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeColor = (status: string): string => {
    const statusLower = status?.toLowerCase() ?? '';
    if (statusLower.includes('approved') || statusLower === 'active') {
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    }
    if (statusLower.includes('submitted') || statusLower.includes('pending')) {
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    }
    if (statusLower.includes('rejected') || statusLower.includes('revoked')) {
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    }
    if (statusLower.includes('draft')) {
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
    return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
  };

  const getTotalApproved = (): number =>
    fundingRequests.reduce((sum, req) => sum + (req.approved_amount ?? 0), 0);

  const getTotalRequested = (): number =>
    fundingRequests.reduce((sum, req) => sum + (req.requested_amount ?? 0), 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <div className="flex items-center justify-between">
            <span>Funding Requests</span>
            {!loading && fundingRequests.length > 0 && (
              <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                {fundingRequests.length} request{fundingRequests.length === 1 ? '' : 's'}
              </span>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-red-600 dark:text-red-400">Error loading funding requests: {error}</p>
          </div>
        ) : fundingRequests.length === 0 ? (
          <div className="text-center py-8">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              No funding requests associated with this project
            </p>
            <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
              Associate funding requests from the Funding page
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {(getTotalRequested() > 0 || getTotalApproved() > 0) && (
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                  <p className="text-sm font-medium text-blue-700 dark:text-blue-300">Total Requested</p>
                  <p className="text-2xl font-bold text-blue-900 dark:text-blue-100 mt-1">
                    {formatCurrency(getTotalRequested())}
                  </p>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
                  <p className="text-sm font-medium text-green-700 dark:text-green-300">Total Approved</p>
                  <p className="text-2xl font-bold text-green-900 dark:text-green-100 mt-1">
                    {formatCurrency(getTotalApproved())}
                  </p>
                </div>
              </div>
            )}

            <div className="space-y-3">
              {fundingRequests.map((request) => (
                <div
                  key={request.id}
                  className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        {request.name ?? 'Unnamed Request'}
                      </h4>
                      {request.program_name && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {request.program_name}
                        </p>
                      )}
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusBadgeColor(request.status)}`}>
                      {request.status}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {request.requested_amount != null && (
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Requested:</span>
                        <span className="ml-2 font-medium text-gray-900 dark:text-white">
                          {formatCurrency(request.requested_amount)}
                        </span>
                      </div>
                    )}
                    {request.approved_amount != null && (
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Approved:</span>
                        <span className="ml-2 font-medium text-green-600 dark:text-green-400">
                          {formatCurrency(request.approved_amount)}
                        </span>
                      </div>
                    )}
                    {request.submission_date && (
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Submitted:</span>
                        <span className="ml-2 font-medium text-gray-900 dark:text-white">
                          {formatDate(request.submission_date)}
                        </span>
                      </div>
                    )}
                    {request.approval_date && (
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Approved:</span>
                        <span className="ml-2 font-medium text-gray-900 dark:text-white">
                          {formatDate(request.approval_date)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FundingRequestsSection;
